import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import moment from 'moment';
import { Skeleton, Badge, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setSelectedOrganization } from 'redux/reducers/globals/globalActions';
import building from 'assets/images/building.jpg';
import { setLoginStatus } from 'redux/reducers/login/loginActions';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { applicationLogout } from 'services/LoginServices';
import { API_STATUS } from 'constants/app-constants';
import AddModal from 'components/add-modal';
import AddOrganization from './add-organization';
import CloneOrganization from './clone-organization';

const OrganizationCard = ({ details, isSuperAdmin, onSelect, onClone }) => (
	<Badge.Ribbon style={{ display: details?.defaultOrg ? '' : 'none' }} text="Default" placement="start">
		<Card
			className="box_shadow"
			style={{ marginTop: '10px', marginBottom: '10px', cursor: 'pointer' }}
			onClick={() => onSelect(details)}>
			<Row style={{ alignItems: 'center', padding: 10, margin: 0 }}>
				<Col xs={3} md={2}>
					<div style={{ backgroundColor: '#f1f2f6', padding: '10px', display: 'flex', justifyContent: 'center' }}>
						<img
							src={building}
							style={{ height: 80, width: '70%', filter: 'opacity(0.2)' }}
							alt="org-building"
						/>
					</div>
				</Col>
				<Col xs={9} md={7} style={{ padding: '10px' }}>
					<h5 style={{ color: '#3f8dfa', marginBottom: 8 }}>{details?.orgName}</h5>
					<span style={{ fontSize: '11px' }}>Organization Id : {details?.orgNumber || details?.id}</span>
					<br />
					<span style={{ fontSize: '11px' }}>GST : {details?.gstin || details?.edition}</span>
					<br />
					{details?.createdAt && (
						<span style={{ fontSize: '11px' }}>
							Created On : {moment(details.createdAt).format('DD/MM/YYYY')}
						</span>
					)}
					{details?.userRole && (
						<>
							<br />
							<span>
								You are an <b>{details.userRole}</b> in this organization
							</span>
						</>
					)}
				</Col>
				<Col xs={12} md={3} style={{ padding: '10px' }}>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
						<Button
							onClick={(e) => {
								e.stopPropagation();
								onSelect(details);
							}}
							size="small"
							type="primary"
							block>
							Go To Organization
						</Button>
						{isSuperAdmin && (
							<Button
								onClick={(e) => {
									e.stopPropagation();
									onClone(details);
								}}
								size="small"
								block>
								Clone
							</Button>
						)}
					</div>
				</Col>
			</Row>
		</Card>
	</Badge.Ribbon>
);

const OrganizationList = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const loginRedux = useSelector((state) => state.loginRedux);
	const globalRedux = useSelector((state) => state.globalRedux);
	const [visible, toggleVisible] = useState(false);
	const [cloneVisible, toggleCloneVisible] = useState(false);
	const [cloneSourceOrg, setCloneSourceOrg] = useState(null);
	const isSuperAdmin = loginRedux?.role === 'superAdmin' || globalRedux?.isSuperAdmin;

	useEffect(() => {
		if (!loginRedux.isLogged) {
			navigate('/login');
		}
		if (loginRedux.isLogged && !loginRedux.isProfileUpdated) {
			navigate('/complete-your-profile');
		}
	}, [loginRedux.isLogged, loginRedux.isProfileUpdated, navigate]);

	useEffect(() => {
		dispatch(getApi('GET_ALL_ORGANIZATION'));
	}, [dispatch]);

	const refreshList = () => {
		dispatch(getApi('GET_ALL_ORGANIZATION'));
		toggleVisible(false);
	};

	const openCloneModal = (org) => {
		setCloneSourceOrg(org);
		toggleCloneVisible(true);
	};

	const closeCloneModal = () => {
		toggleCloneVisible(false);
		setCloneSourceOrg(null);
	};

	const refreshAfterClone = () => {
		dispatch(getApi('GET_ALL_ORGANIZATION'));
		closeCloneModal();
	};

	const selectOrganization = (org) => {
		dispatch(setLoginStatus(true));
		dispatch(setSelectedOrganization(org));
		navigate('/');
	};

	let organizationDetails = useSelector((state) => state?.globalRedux?.organizations || []);
	organizationDetails = organizationDetails.sort((a, b) => b.defaultOrg - a.defaultOrg);

	return (
		<Container>
			<Row>
				<Col lg={9} md={12} xl={9} style={{ marginLeft: 'auto', marginRight: 'auto', alignItems: 'center', padding: '10px 0' }}>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<h6 style={{ margin: 0 }}>
							Hi {loginRedux.firstName} {loginRedux.lastName},
						</h6>
						<div>
							<Button
								size="sm"
								type="primary"
								onClick={() => {
									applicationLogout();
								}}>
								Logout
							</Button>
						</div>
					</div>
					<div style={{ borderBottom: '1px solid #3f8dfa', marginTop: '5px' }}></div>
				</Col>
			</Row>

			<Row>
				<Col lg={9} md={12} xl={9} style={{ marginLeft: 'auto', marginRight: 'auto' }}>
					{!!!organizationDetails.length && globalRedux.apiStatus.GET_ALL_ORGANIZATION === API_STATUS.PENDING && <Skeleton active />}
					{!!!organizationDetails.length && globalRedux.apiStatus.GET_ALL_ORGANIZATION === API_STATUS.SUCCESS && (
						<div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No organizations</div>
					)}

					{organizationDetails?.map((details, index) => (
						<OrganizationCard
							key={details._id || details.id || index}
							details={details}
							isSuperAdmin={isSuperAdmin}
							onSelect={selectOrganization}
							onClone={openCloneModal}
						/>
					))}
				</Col>
			</Row>
			<AddModal {...{ visible, toggleVisible, modalTitle: 'Create Organization', width: '25%' }}>
				<AddOrganization {...{ refreshList, closeModal: () => toggleVisible(false) }} />
			</AddModal>
			<AddModal {...{ visible: cloneVisible, toggleVisible: closeCloneModal, modalTitle: 'Clone Organization', width: '30%' }}>
				{cloneSourceOrg && (
					<CloneOrganization {...{ sourceOrg: cloneSourceOrg, refreshList: refreshAfterClone, closeModal: closeCloneModal }} />
				)}
			</AddModal>
		</Container>
	);
};

export default OrganizationList;
